// apps/web/src/features/cargo/components/CargoPaymentDrawer.tsx
//
// Document-gated client-payment drawer (#1938), opened from the client-paid cell
// for anyone who can view the row. Replaces the legacy `CargoPaymentModal`: it is
// an `EditorDrawer` (auto-wired unsaved-changes guard) projected by role —
//   - financier (`cargo.clientPayment.review`): per-document accept/reject review;
//   - manager (responsible actor + `cargo.clientPayment.manage`): desired-status
//     submit for review;
//   - everyone else with `cargo.read`: read-only status + downloadable doc list.
// File attach/delete is a separate immediate mutation (never part of `dirty`);
// delete is enabled only for `pending` documents (see `CargoPaymentDocList`).

import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CARGO_CLIENT_PAYMENT_STATUS_LABELS, type CargoArticleWire } from '@magimp/shared';

import { Button, EditorDrawer, IconButton, useEditorGuard } from '../../../ui/index.js';
import { useAuthStore } from '../../../stores/auth.js';
import { useToastStore } from '../../../stores/toast.js';
import { errorMessage } from '../../../api/errors.js';
import {
  canManagePaymentDocs,
  canReviewClientPayment,
  canSaveGatedClientPayment,
  type CargoActor,
} from '../ability.js';
import { useCargoPaymentDocuments, useCreateCargoPaymentDocument } from '../mutations.js';
import {
  CARGO_PAYMENT_DOC_ACCEPT,
  uploadCargoPaymentDoc,
  validateCargoPaymentDoc,
  type CargoPaymentDocError,
} from '../forms/useCargoPaymentDocUpload.js';
import { CargoPaymentDocList } from './CargoPaymentDocList.js';
import { CargoPaymentManagerView } from './CargoPaymentManagerView.js';
import { CargoPaymentFinancierView } from './CargoPaymentFinancierView.js';

export interface CargoPaymentDrawerProps {
  row: CargoArticleWire;
  onClose: () => void;
  readOnlyMirror: boolean;
}

export function CargoPaymentDrawer({ row, onClose, readOnlyMirror }: CargoPaymentDrawerProps) {
  const { t } = useTranslation(['cargo', 'common']);
  const user = useAuthStore((s) => s.user);
  const permissions = useAuthStore((s) => s.permissions);
  const actor: CargoActor = { user, permissions };

  const docsQuery = useCargoPaymentDocuments(row.id, true);
  const documents = docsQuery.data?.documents ?? [];
  const hasActiveDocument = documents.length > 0;

  // Role projection. The read-only mirror suppresses ALL write affordances.
  const canReview = canReviewClientPayment(actor) && !readOnlyMirror;
  const canManage = canSaveGatedClientPayment(actor, row) && !readOnlyMirror;
  const canManageDocs = canManagePaymentDocs(actor, row) && !readOnlyMirror;

  // A single dirty/busy mirror feeds the EditorDrawer guard; the active view
  // bubbles its own state up. File add/remove are immediate mutations, so they
  // contribute to `busy` (via the upload control) but never to `dirty`.
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  return (
    <EditorDrawer width={560} dirty={dirty} busy={busy} onConfirmClose={onClose}>
      <Header title={t('cargo:payment.title', { article: row.article })} onClose={onClose} />
      <div style={{ flex: 1, padding: '16px 20px', overflow: 'auto', minHeight: 0 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <section style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>
              {t('cargo:payment.statusLabel')}
            </span>
            <span
              data-testid="cargo-payment-status-readonly"
              style={{ fontSize: 13, color: 'var(--fg-1)' }}
            >
              {CARGO_CLIENT_PAYMENT_STATUS_LABELS[row.paidByClientStatus]}
            </span>
            {canManage && (
              <CargoPaymentManagerView
                cargoId={row.id}
                paidByClientStatus={row.paidByClientStatus}
                hasActiveDocument={hasActiveDocument}
                onDirtyChange={setDirty}
                onBusyChange={setBusy}
                onSubmitted={onClose}
              />
            )}
          </section>

          <section style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--fg-2)' }}>
              {t('cargo:payment.documents.title')}
            </span>
            {docsQuery.isLoading ? (
              <span style={{ fontSize: 13, color: 'var(--fg-3)' }}>{t('cargo:loading')}</span>
            ) : docsQuery.isError ? (
              <span role="alert" style={{ fontSize: 13, color: 'var(--status-error-fg)' }}>
                {t('cargo:payment.documents.error')}
              </span>
            ) : (
              <CargoPaymentDocList
                cargoId={row.id}
                documents={documents}
                canManage={canManageDocs}
              />
            )}
            {canManageDocs && <UploadControl cargoId={row.id} onBusyChange={setBusy} />}
          </section>

          {canReview && !docsQuery.isLoading && !docsQuery.isError && (
            <section>
              <CargoPaymentFinancierView
                cargoId={row.id}
                documents={documents}
                onDirtyChange={setDirty}
                onBusyChange={setBusy}
                onSaved={onClose}
              />
            </section>
          )}
        </div>
      </div>
      <Footer onClose={onClose} />
    </EditorDrawer>
  );
}

function Header({ title, onClose }: { title: string; onClose: () => void }) {
  const { t } = useTranslation(['common']);
  // Route the explicit close through the editor guard so an unsaved manager/
  // financier edit surfaces the discard prompt (matching Escape/backdrop).
  const { confirmDestructive } = useEditorGuard();
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '16px 20px',
        borderBottom: '1px solid var(--border-1)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, color: 'var(--fg-1)' }}>{title}</h2>
      <IconButton
        icon="x"
        label={t('common:actions.close')}
        onClick={() => confirmDestructive(onClose)}
        data-testid="cargo-payment-close"
      />
    </div>
  );
}

function Footer({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation(['common']);
  const { confirmDestructive } = useEditorGuard();
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'flex-end',
        padding: '12px 20px',
        borderTop: '1px solid var(--border-1)',
      }}
    >
      <Button kind="ghost" onClick={() => confirmDestructive(onClose)}>
        {t('common:actions.close')}
      </Button>
    </div>
  );
}

function UploadControl({
  cargoId,
  onBusyChange,
}: {
  cargoId: string;
  onBusyChange: (busy: boolean) => void;
}) {
  const { t } = useTranslation(['cargo']);
  const showToast = useToastStore((s) => s.show);
  const create = useCreateCargoPaymentDocument(cargoId);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function docErrorText(error: CargoPaymentDocError): string {
    return t(`cargo:payment.documents.errors.${error}`);
  }

  async function onFile(file: File) {
    const v = validateCargoPaymentDoc(file);
    if (!v.ok && v.error) {
      showToast({ tone: 'error', text: docErrorText(v.error) });
      return;
    }
    setUploading(true);
    onBusyChange(true);
    try {
      // Upload (presign + PUT) first; only the issued objectKey binds via the
      // mutation — this upload→bind ordering is the contract under test.
      const uploaded = await uploadCargoPaymentDoc(file);
      await create.mutateAsync(uploaded);
      showToast({ tone: 'success', text: t('cargo:payment.documents.toasts.uploaded') });
    } catch (err) {
      showToast({ tone: 'error', text: errorMessage(err) });
    } finally {
      setUploading(false);
      onBusyChange(false);
    }
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept={CARGO_PAYMENT_DOC_ACCEPT}
        disabled={uploading}
        onChange={(e) => {
          const file = e.currentTarget.files?.[0];
          e.currentTarget.value = '';
          if (file) void onFile(file);
        }}
        style={{ display: 'none' }}
        data-testid="cargo-payment-doc-input"
      />
      <Button
        kind="secondary"
        size="sm"
        icon="upload"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        data-testid="cargo-payment-doc-upload"
      >
        {uploading ? t('cargo:payment.documents.uploading') : t('cargo:payment.documents.upload')}
      </Button>
    </div>
  );
}
